import React, { useCallback, useEffect, useRef } from "react";
import { useSelector } from "store/useSelector";

import type * as types from "../types/types";
import {
  askrClients,
  PRODUCTION_SEGMENTS_QUERY,
} from "../providers/AskrProvider";
import {
  useFocusRange,
  useIsSingleProcessPage,
  useVisibleProcesses,
} from "utils/customHooks/genericHooks";
import { isEqual } from "lodash";
import { useExtraQueryOptions } from "utils/useQuery";
import { useLazyQuery } from "@apollo/client";

// Throttling delay in milliseconds - wait this long after the last wheel event
const THROTTLE_WAIT = 1000; // 1 second - wait for wheel events to stop

// The props we expect to be passed to the exported component.
interface SegmentsGetterStaticProps {
  process: types.Process;
  queuedSegmentsRef: React.MutableRefObject<
    { processId: string; segments: types.ProductionSegmentType[] }[]
  >;
  setError: (error: Error) => void;
}

/** simplified version of SegmentsGetter optimized for fetching past data only rather than current updating data */
const SegmentsGetterStaticPastDataOnly = ({
  process,
  queuedSegmentsRef,
  setError,
}: // tslint:disable-next-line
SegmentsGetterStaticProps) => {
  const focusRange = useFocusRange();
  const visibleRange = useSelector(state => state.legacy.visibleRange);
  const isSingleProcessPage = useIsSingleProcessPage();
  const [singleProcess] = useVisibleProcesses({ getSingleProcessOnly: true });

  // Skip unnecessary fetching if we're on a single process page but this is a different process
  const shouldSkip =
    isSingleProcessPage && singleProcess && process.id !== singleProcess.id;

  const smallestGe =
    visibleRange && focusRange
      ? Math.min(visibleRange.ge, focusRange.ge)
      : visibleRange?.ge || focusRange?.ge || null;

  const biggestLt =
    visibleRange && focusRange
      ? Math.max(visibleRange.lt, focusRange.lt)
      : visibleRange?.lt || focusRange?.lt || null;

  const ge = smallestGe;
  const lt = biggestLt;

  const ready = ge && lt && !shouldSkip;

  return ready ? (
    <Query
      {...{
        queuedSegmentsRef,
        process,
        setError,
      }}
    />
  ) : null;
};

function Query({
  queuedSegmentsRef,
  process,
  setError,
}: SegmentsGetterStaticProps) {
  // Core dependencies and state
  const focusRange = useFocusRange();
  const visibleRange = useSelector(state => state.legacy.visibleRange);
  const isSingleProcessPage = useIsSingleProcessPage();
  const [singleProcess] = useVisibleProcesses({ getSingleProcessOnly: true });

  // Skip unnecessary fetching if we're on a single process page but this is a different process
  const shouldSkip =
    isSingleProcessPage && singleProcess && process.id !== singleProcess.id;

  const askrClient = askrClients.get(process.ckey);
  const extraQueryOptions = useExtraQueryOptions();

  // Refs for throttling
  const throttleTimerRef = useRef<number | null>(null);
  const lastRangeStringRef = useRef("");
  const pendingRangeRef = useRef<{ ge: string; lt: string } | null>(null);

  // Calculate range values
  const smallestGe =
    visibleRange && focusRange
      ? Math.min(visibleRange.ge, focusRange.ge)
      : visibleRange?.ge || focusRange?.ge || null;
  const biggestLt =
    visibleRange && focusRange
      ? Math.max(visibleRange.lt, focusRange.lt)
      : visibleRange?.lt || focusRange?.lt || null;
  const ge = String(smallestGe);
  const lt = String(biggestLt);

  // Set up the lazy query with callbacks
  const [fetchSegments] = useLazyQuery(PRODUCTION_SEGMENTS_QUERY, {
    client: askrClient,
    fetchPolicy: extraQueryOptions.fetchPolicy,
    onCompleted: data => {
      if (data.errors) {
        console.error(data.errors);
        setError(data.errors);
      }
      if (data?.productionSegments) {
        queueNewSegments({
          processId: process.id,
          newSegments: data.productionSegments,
        });
        console.log(
          `[SegmentsGetter] Query completed successfully for ${process.id} with ${data.productionSegments.length} segments`
        );
      } else {
        console.log(
          `[SegmentsGetter] ${process.id} (${process.name}) - No data returned from query -> trying old SegmentsGetter`
        );
        console.log(data);
        setError(
          new Error(
            `SegmentsGetterStaticPastDataOnly - ${process.id} (${process.name}) - No data returned from query`
          )
        );
      }
    },
    onError: err => {
      console.error(`[SegmentsGetter] Error for ${process.id}:`, err);
      setError(err);
    },
  });

  // Helper to queue new segments
  const queueNewSegments = useCallback(
    ({ processId, newSegments }) => {
      queuedSegmentsRef.current = [
        ...queuedSegmentsRef.current,
        { processId, segments: newSegments },
      ];
    },
    [queuedSegmentsRef]
  );

  // Throttling effect for query execution
  useEffect(() => {
    // Skip if invalid range or if we're on a single process page and this isn't that process
    if (!ge || !lt || shouldSkip) {
      console.log(
        `[SegmentsGetter] Skipping query for ${process.id} - on single process page ${singleProcess?.id}`
      );
      return;
    }

    // Store the current range as pending
    const currentRange = { ge, lt };
    pendingRangeRef.current = currentRange;

    // If there's already a timer running, let it complete with the latest range
    if (throttleTimerRef.current !== null) {
      return;
    }

    // Start a new timer
    console.log(`[SegmentsGetter] Starting throttle timer for ${process.id}`);

    throttleTimerRef.current = window.setTimeout(() => {
      // Get the most recent range requested during throttle period
      const finalRange = pendingRangeRef.current;
      if (!finalRange) {
        throttleTimerRef.current = null;
        return;
      }

      // Reset pending range
      pendingRangeRef.current = null;

      // Generate unique string for deduplication
      const rangeString = `${process.id}_${finalRange.ge}_${finalRange.lt}`;

      // Skip duplicate queries
      if (rangeString === lastRangeStringRef.current) {
        console.log(
          `[SegmentsGetter] Skipping duplicate query for ${process.id}`
        );
        throttleTimerRef.current = null;
        return;
      }

      // Execute the query with final range
      console.log(
        `[SegmentsGetter] Throttle complete, executing query for ${process.id} with range:`,
        finalRange
      );
      console.log(
        `[SegmentsGetter] Is single process page: ${isSingleProcessPage}, process ID: ${process.id}, single process ID: ${singleProcess?.id}`
      );
      lastRangeStringRef.current = rangeString;

      const variables = {
        machemetrySourceName: process.id,
        ...finalRange,
      };

      fetchSegments({ variables });

      // Clear the timer reference
      throttleTimerRef.current = null;
    }, THROTTLE_WAIT);

    // No cleanup needed - let the timer complete
    return () => {};
  }, [
    focusRange,
    visibleRange,
    process.id,
    fetchSegments,
    shouldSkip,
    singleProcess?.id,
    isSingleProcessPage,
    ge,
    lt,
  ]);

  return null;
}

export default React.memo(SegmentsGetterStaticPastDataOnly, isEqual);
